import { useState } from 'react';
import { ChevronDown, Tag, Trash2, Users, X } from 'lucide-react';
import {
  RSVP_RESPONSES,
  type GuestCapabilities,
  type GuestFormOptions,
  type RsvpResponseValue,
} from '@peles/shared';
import { DropdownMenu } from '../../common/DropdownMenu';
import { Popover } from '../../common/Popover';
import { useBulkDeleteEventGuests, useBulkEditEventGuests } from '../../../hooks/useEventGuests';
import type { GuestBulkEdit } from '../../../api/eventGuests';
import { useDialog } from '../../../context/DialogContext';
import { extractApiErrorMessage } from '../../../utils/apiError';
import { SeatPicker } from './SeatPicker';
import { useSeatGuests } from './useSeatGuests';

export interface GuestBulkBarProps {
  eventId: string;
  selectedIds: string[];
  capabilities: GuestCapabilities;
  options: GuestFormOptions | undefined;
  onClear: () => void;
}

const buttonClass =
  'inline-flex items-center gap-1 px-3 py-2 min-h-[40px] rounded-lg text-sm font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50';

/**
 * What to do with the guests ticked in the list — only the actions the caller
 * may take. On a phone it docks to the bottom of the screen (the list leaves
 * room for it); on a desktop it sits above the list.
 */
export function GuestBulkBar({
  eventId,
  selectedIds,
  capabilities,
  options,
  onClear,
}: GuestBulkBarProps) {
  const bulkEdit = useBulkEditEventGuests(eventId);
  const bulkDelete = useBulkDeleteEventGuests(eventId);
  const { seat, isPending: seating } = useSeatGuests(eventId);
  const { confirm, alert } = useDialog();
  const [group, setGroup] = useState('');
  const count = selectedIds.length;
  if (count === 0) return null;
  const busy = bulkEdit.isPending || bulkDelete.isPending || seating;

  const apply = async (updates: GuestBulkEdit) => {
    try {
      await bulkEdit.mutateAsync({ guestIds: selectedIds, updates });
      onClear();
    } catch (err) {
      await alert({
        title: 'Could not change the guests',
        message: extractApiErrorMessage(err, 'Nothing was changed.'),
        variant: 'error',
      });
    }
  };

  const remove = async () => {
    const ok = await confirm({
      title: 'Delete guests',
      message: `Delete ${count} guest${count === 1 ? '' : 's'}? This cannot be undone.`,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      await bulkDelete.mutateAsync(selectedIds);
      onClear();
    } catch (err) {
      await alert({
        title: 'Could not delete',
        message: extractApiErrorMessage(err, 'Nothing was deleted.'),
        variant: 'error',
      });
    }
  };

  const seatAll = async (tableNumber: number | null) => {
    if (await seat({ guestIds: selectedIds, tableNumber })) onClear();
  };

  return (
    <div
      role="region"
      aria-label="Selected guests"
      data-testid="guest-bulk-bar"
      className="fixed inset-x-0 bottom-0 z-30 md:sticky md:top-2 md:bottom-auto bg-white border-t md:border border-gray-200 md:rounded-2xl shadow-lg px-3 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:pb-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-gray-900 mr-1">{count} selected</span>
        {capabilities.rsvp && (
          <DropdownMenu
            ariaLabel="Set RSVP"
            trigger={(props) => (
              <button type="button" {...props} disabled={busy} className={buttonClass}>
                RSVP <ChevronDown size={14} aria-hidden />
              </button>
            )}
            items={RSVP_RESPONSES.map((o) => ({
              id: o.value,
              label: o.label,
              onSelect: () => apply({ response: o.value as RsvpResponseValue }),
            }))}
          />
        )}
        {capabilities.write && (options?.sides.length ?? 0) > 0 && (
          <DropdownMenu
            ariaLabel="Set side"
            trigger={(props) => (
              <button type="button" {...props} disabled={busy} className={buttonClass}>
                Side <ChevronDown size={14} aria-hidden />
              </button>
            )}
            items={[
              ...(options?.sides ?? []).map((o) => ({
                id: o.value,
                label: o.label,
                onSelect: () => apply({ side: o.value }),
              })),
              {
                id: '__none',
                label: 'No side',
                dividerBefore: true,
                onSelect: () => apply({ side: null }),
              },
            ]}
          />
        )}
        {capabilities.write && (
          <Popover
            ariaLabel="Set family group"
            trigger={(props) => (
              <button type="button" {...props} disabled={busy} className={buttonClass}>
                <Users size={14} aria-hidden /> Family
              </button>
            )}
          >
            {(close) => (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  close();
                  void apply({ familyGroup: group.trim() || null });
                  setGroup('');
                }}
                className="space-y-2"
              >
                <label className="block text-sm font-medium text-gray-700" htmlFor="bulk-family">
                  Family group
                </label>
                <input
                  id="bulk-family"
                  list="bulk-family-options"
                  value={group}
                  onChange={(e) => setGroup(e.target.value)}
                  placeholder="Leave empty to clear"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm"
                />
                <datalist id="bulk-family-options">
                  {(options?.familyGroups ?? []).map((g) => (
                    <option key={g} value={g} />
                  ))}
                </datalist>
                <button
                  type="submit"
                  className="w-full px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium"
                >
                  Apply
                </button>
              </form>
            )}
          </Popover>
        )}
        {capabilities.write && (options?.tags.length ?? 0) > 0 && (
          <Popover
            ariaLabel="Add or remove tags"
            panelClassName="w-72"
            trigger={(props) => (
              <button type="button" {...props} disabled={busy} className={buttonClass}>
                <Tag size={14} aria-hidden /> Tags
              </button>
            )}
          >
            {(close) => (
              <ul className="space-y-1">
                {(options?.tags ?? [])
                  .filter((t) => capabilities.producerFields || t.value !== 'vip')
                  .map((t) => (
                    <li key={t.value} className="flex items-center justify-between gap-2">
                      <span className="text-sm text-gray-800">{t.label}</span>
                      <span className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            close();
                            void apply({ addTags: [t.value] });
                          }}
                          className="px-2 py-1 text-xs rounded-md border border-gray-300 hover:bg-gray-50"
                          aria-label={`Add ${t.label}`}
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            close();
                            void apply({ removeTags: [t.value] });
                          }}
                          className="px-2 py-1 text-xs rounded-md border border-gray-300 hover:bg-gray-50"
                          aria-label={`Remove ${t.label}`}
                        >
                          Remove
                        </button>
                      </span>
                    </li>
                  ))}
              </ul>
            )}
          </Popover>
        )}
        {capabilities.seating && (
          <>
            <SeatPicker
              eventId={eventId}
              tableNumber={null}
              partySize={count}
              label="Seat at table"
              onChoose={(table) => void seatAll(table)}
              disabled={busy}
            />
            <button
              type="button"
              onClick={() => void seatAll(null)}
              disabled={busy}
              className={buttonClass}
            >
              Unseat
            </button>
          </>
        )}
        {capabilities.delete && (
          <button
            type="button"
            onClick={() => void remove()}
            disabled={busy}
            className={`${buttonClass} text-red-700 border-red-200 hover:bg-red-50`}
          >
            <Trash2 size={14} aria-hidden /> Delete
          </button>
        )}
        <button
          type="button"
          onClick={onClear}
          aria-label="Clear selection"
          className="ml-auto p-2 rounded-lg text-gray-500 hover:bg-gray-100"
        >
          <X size={18} aria-hidden />
        </button>
      </div>
    </div>
  );
}
